import { GRAPH_DEFAULT_ZOOM_FACTOR } from '@common/constants';
import { useSpecs } from '@common/graph/gem-specs';
import { useChangeGraphPath, useProcessDialogHistory } from '@common/historyHooks';
import { useIdeActions } from '@common/ide/state';
import { useCurrentGraph } from '@common/useOriginalGraph';
import { Ellipsis } from '@prophecy/ui-v3';
import { matchesPropertyPath } from '@prophecy/utils/data';
import { memo, ReactNode } from 'react';

import { SettingsKeys, workflowDropdownOpts } from '../../constants/workflow';
import { LogLineAndColumn } from '../../generic-components/Logger/LogLineAndCol';
import { Log } from '../../generic-components/Logger/types';
import {
  getGraphPathAndProcess,
  getMetaInfoOrMetadataPropertyKey,
  getProcessesPath,
  isSubGraphOrMetaGem
} from '../../Parser/bindings';
import { FunctionType } from '../../redux/types';
import { StyledLabel, StyledLogButton } from './Canvas';
import { Footer } from './Footer/Footer';
import { useFunctionLabelAndNavigation } from './Functions/utils';

export const PipelineFooter = memo(() => {
  const specs = useSpecs();
  const { rootGraph: workflow, rootPath } = useCurrentGraph();
  const changeGraphPath = useChangeGraphPath();
  const { openDialog } = useProcessDialogHistory();
  const getFunctionLabelAndNavigation = useFunctionLabelAndNavigation();
  const { focusNodeInGraph } = useIdeActions();

  const { toggleActiveDialog } = useIdeActions();

  function onLogClick(property: string) {
    if (property) {
      const { graphPath, process } = getGraphPathAndProcess(property, workflow, rootPath);
      const componentId = process.id;
      const processPath = `${getProcessesPath(graphPath)}.${componentId}`;

      if (isSubGraphOrMetaGem(process, specs)) {
        changeGraphPath(processPath);
        return;
      }

      changeGraphPath(graphPath);
      // if the diagnostic is on the port path, focus the node instead of opening it.
      if (matchesPropertyPath(property, `${processPath}.ports.inputs`)) {
        focusNodeInGraph({ nodePath: processPath, duration: 800, zoom: GRAPH_DEFAULT_ZOOM_FACTOR });
        return;
      }

      if (!isSubGraphOrMetaGem(process, specs)) {
        openDialog(componentId);
      }
    }
  }

  function renderLogLabel(log: Log): ReactNode {
    const { metadata } = log;
    let activeLabel = 'config';

    const getLogUI = (value: string) => {
      return (
        <Ellipsis tooltip={true} tooltipProps={{ placement: 'top', align: 'end' }}>
          {value || <LogLineAndColumn log={log} />}
        </Ellipsis>
      );
    };
    let label = <StyledLabel level='sm'>{getLogUI(activeLabel)}</StyledLabel>;

    const property = metadata?.property as string;

    if (property) {
      const { hasMetaInfoProperty, key, hasMetaDataProperty } = getMetaInfoOrMetadataPropertyKey(
        property,
        workflow,
        rootPath
      );
      let activeDialogKey = '';
      activeLabel = key;
      if (hasMetaInfoProperty) {
        if (key === 'udfs') {
          activeDialogKey = workflowDropdownOpts.udfs;
        } else if (key === 'configuration') {
          activeDialogKey = workflowDropdownOpts.configs;
        } else if (key?.toLowerCase()?.includes('dependencies')) {
          activeDialogKey = workflowDropdownOpts.dependencies;
        } else if (SettingsKeys.includes(key)) {
          activeDialogKey = workflowDropdownOpts.options;
        }
      }
      if (key === 'udfs' || key === 'functions') {
        // find the function index path
        const { name, onClick } = getFunctionLabelAndNavigation(
          key === 'udfs' ? FunctionType.udfs : FunctionType.businessRules,
          property
        );

        label = name ? (
          <StyledLogButton size='s' variant='link' onClick={onClick} data-test-id='pipeline-footer-function-button'>
            {getLogUI(name)}
          </StyledLogButton>
        ) : (
          <StyledLabel level='sm'>{getLogUI(activeLabel)}</StyledLabel>
        );
      } else if (hasMetaInfoProperty || hasMetaDataProperty) {
        label = (
          <StyledLogButton
            size='s'
            variant='link'
            onClick={() => (hasMetaInfoProperty ? toggleActiveDialog(activeDialogKey) : null)}
            data-test-id='pipeline-footer-meta-info-button'>
            {getLogUI(key)}
          </StyledLogButton>
        );
      } else {
        const { process } = getGraphPathAndProcess(property, workflow, rootPath);
        if (process) {
          const metaLabel = process?.metadata?.label;
          label = (
            <StyledLogButton
              size='s'
              variant='link'
              onClick={() => onLogClick(property)}
              data-test-id='pipeline-footer-process-button'>
              {getLogUI(metaLabel)}
            </StyledLogButton>
          );
        }
      }
    }

    return label;
  }
  return <Footer renderLogLabel={renderLogLabel} />;
});
